import React, { useEffect, useRef, useState } from "react";
import { Link } from "../lib/LocalizedLink";
import { X, Check, ArrowRight } from "lucide-react";
import { brand } from "../mock/content";
import { t } from "../lib/ui";
import { track } from "../lib/analytics";
import { getActiveLang } from "../lib/activeLang";

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "torri_hail_popup_seen";
const SUPPRESS_DAYS = 7;
const DELAY_MS = 8000;

const seenRecently = () => {
  try {
    const ts = Number(localStorage.getItem(STORAGE_KEY) || 0);
    return ts && Date.now() - ts < SUPPRESS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
};
const markSeen = () => { try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* ignore */ } };

export default function HailPopup() {
  const [open, setOpen] = useState(false);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ nome: "", telefono: "", email: "", indirizzo: "" });
  const [hp, setHp] = useState("");
  const [consent, setConsent] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  const isIt = getActiveLang() === "it";

  useEffect(() => {
    if (!isIt || seenRecently()) return;
    timerRef.current = setTimeout(() => setOpen(true), DELAY_MS);
    return () => clearTimeout(timerRef.current);
  }, [isIt]);

  useEffect(() => {
    if (!open) { setShow(false); return; }
    markSeen();
    const raf = requestAnimationFrame(() => setShow(true));
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.nome || !form.telefono || !form.email) { setError("Compila nome, telefono ed email."); return; }
    if (!form.indirizzo) { setError("Inserisci l'indirizzo."); return; }
    if (!consent) { setError("Devi accettare l'informativa privacy."); return; }
    setLoading(true);
    try {
      const messaggio = "Richiesta consulenza resistenza grandine (PRISMA 4.0).";
      const res = await fetch(`${API}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tipo: "Privato",
          nome: form.nome,
          email: form.email,
          telefono: form.telefono,
          indirizzo: form.indirizzo,
          messaggio,
          hp,
          source: "Popup Grandine PRISMA · Homepage",
          productName: "PRISMA 4.0",
        }),
      });
      if (!res.ok) throw new Error("bad");
      setSent(true);
      track("generate_lead", { form_type: "Privato", source: "Popup Grandine PRISMA · Homepage", product_name: "PRISMA 4.0" });
      setTimeout(() => setOpen(false), 3500);
    } catch {
      setError("Invio non riuscito. Riprova tra poco.");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const inp = "w-full bg-white/[0.04] border border-white/15 px-3.5 py-2.5 text-sm text-white rounded focus:border-[#F9E212] outline-none placeholder:text-white/30";

  return (
    <div className="fixed inset-0 z-[120] overflow-y-auto overscroll-contain" data-testid="hail-popup" role="dialog" aria-modal="true">
      <div className={`fixed inset-0 bg-black/85 backdrop-blur-md transition-opacity duration-300 ${show ? "opacity-100" : "opacity-0"}`} onClick={() => setOpen(false)} />
      <div className="relative min-h-full flex items-center justify-center p-4">
        <div className={`relative w-[min(960px,96vw)] bg-[#0a0a0a] border border-white/10 rounded-xl overflow-hidden transition-all duration-300 ${show ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-4 scale-[0.98]"}`}>
          <button onClick={() => setOpen(false)} aria-label="Chiudi" data-testid="hail-popup-close" className="absolute top-3 right-3 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-black/60 border border-white/20 text-white/70 hover:text-white hover:border-white/50 transition-colors">
            <X size={18} />
          </button>

          <div className="grid md:grid-cols-2">
            {/* Colonna video + copy tecnico */}
            <div className="relative">
              <video
                className="w-full h-56 md:h-full object-cover"
                src="/tech/prisma-hail.mp4"
                autoPlay muted loop playsInline
                data-testid="hail-popup-video"
              />
              <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-black/90 via-black/40 to-transparent" />
              <div className="absolute bottom-0 left-0 p-6 md:p-8">
                <span className="block text-[#F9E212] font-black uppercase tracking-tight leading-[0.95]" style={{ fontFamily: "Archivo", fontSize: "clamp(1.9rem, 4.5vw, 3.2rem)" }}>
                  Tecnologia<br />PRISMA 4.0
                </span>
                <p className="mt-3 text-white font-black leading-[1.05] text-xl md:text-2xl" style={{ fontFamily: "Archivo" }}>
                  Resistenza alla grandine <span style={{ color: brand.yellow }}>certificata</span>
                </p>
              </div>
            </div>

            {/* Colonna contenuto + form */}
            <div className="p-6 md:p-8">
              {sent ? (
                <div className="h-full flex flex-col items-center justify-center text-center py-10" data-testid="hail-popup-success">
                  <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4" style={{ background: brand.yellow }}>
                    <Check size={26} className="text-black" />
                  </div>
                  <p className="text-white text-lg font-semibold" style={{ fontFamily: "Archivo" }}>Richiesta inviata</p>
                  <p className="text-white/60 text-sm mt-2">Ti ricontattiamo al più presto con la scheda tecnica PRISMA 4.0.</p>
                </div>
              ) : (
                <>
                  <p className="text-white/85 text-sm leading-relaxed">
                    {t("hp_res_p1")}
                  </p>
                  <p className="mt-3 text-white/70 text-[13px] leading-relaxed">
                    {t("hp_res_p2")}
                  </p>
                  <p className="mt-4 font-black uppercase tracking-tight leading-[1.05] text-lg md:text-xl" style={{ fontFamily: "Archivo", color: brand.yellow }}>
                    {t("hp_res_line")}
                  </p>
                  <p className="mt-3 text-white/85 text-[13px] leading-relaxed">
                    {t("hp_res_p3")}
                  </p>

                  <form onSubmit={submit} className="mt-5 space-y-3" data-testid="hail-popup-form">
                    <input type="text" name="website" tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)} className="hidden" aria-hidden="true" />
                    <input data-testid="hail-nome" className={inp} placeholder="Nome e cognome *" value={form.nome} onChange={update("nome")} />
                    <input data-testid="hail-telefono" className={inp} placeholder="Telefono *" value={form.telefono} onChange={update("telefono")} />
                    <HailAddress value={form.indirizzo} onChange={(v) => setForm((f) => ({ ...f, indirizzo: v }))} className={inp} />
                    <input data-testid="hail-email" type="email" className={inp} placeholder="Email *" value={form.email} onChange={update("email")} />
                    <label className="flex items-start gap-2 cursor-pointer text-white/55 text-xs">
                      <input data-testid="hail-consent" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="accent-yellow-400 w-4 h-4 mt-0.5" />
                      <span>Ho letto e accetto l'<Link to="/privacy" target="_blank" className="underline hover:text-white">informativa privacy</Link>.</span>
                    </label>
                    {error && <p className="text-red-400 text-xs" data-testid="hail-error">{error}</p>}
                    <button type="submit" disabled={loading} data-testid="hail-submit" className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 text-black text-sm uppercase tracking-[0.12em] font-semibold rounded transition-transform hover:scale-[1.02] disabled:opacity-60" style={{ background: brand.yellow, fontFamily: "Space Grotesk" }}>
                      {loading ? "Invio…" : "Richiedi consulenza gratuita"} <ArrowRight size={16} />
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function HailAddress({ value, onChange, className }) {
  const [q, setQ] = useState(value || "");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const token = useRef(Math.random().toString(36).slice(2));

  useEffect(() => { setQ(value || ""); }, [value]);

  const onInput = (val) => {
    setQ(val);
    onChange(val);
    if (timer.current) clearTimeout(timer.current);
    if (val.trim().length < 3) { setItems([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/api/geo/autocomplete?q=${encodeURIComponent(val)}&session_token=${token.current}`);
        const data = await res.json();
        setItems(data.items || []);
        setOpen((data.items || []).length > 0);
      } catch { setItems([]); }
    }, 350);
  };

  const pick = (it) => { onChange(it.label); setQ(it.label); setItems([]); setOpen(false); };

  return (
    <div className="relative">
      <input
        data-testid="hail-indirizzo"
        className={className}
        placeholder="Indirizzo *"
        value={q}
        autoComplete="off"
        onChange={(e) => onInput(e.target.value)}
        onFocus={() => items.length && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && items.length > 0 && (
        <ul className="absolute z-30 left-0 right-0 mt-1 bg-[#141414] border border-white/15 rounded shadow-lg max-h-56 overflow-auto" data-testid="hail-address-suggestions">
          {items.map((it, i) => (
            <li key={it.place_id || i}>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); pick(it); }}
                data-testid={`hail-address-suggestion-${i}`}
                className="w-full text-left px-3.5 py-2 text-xs text-white/80 hover:bg-white/10 transition-colors"
              >
                {it.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
